<template>
  <a-layout v-if="appReady" class="appLayout">
    <a-layout-header class="appHeader">
      <div v-if="webSystemSetting?.topBackground" class="wrap-topHead">
        <img class="topHeadImg" :src="topBackground" alt="" />
      </div>
    </a-layout-header>
    <div class="wrap-navigation" id="navigationWrapper">
      <Navigation></Navigation>
    </div>
    <a-layout-content class="appContent">
      <div class="mainBody">
        <div v-if="spinning" class="mainLoading">
          <a-spin tip="加载中..." size="large" :spinning="spinning"> </a-spin>
        </div>
        <router-view />
      </div>
      <div class="mainAffix">
        <!-- 支付宝小程序二维码 -->
        <div v-if="webSystemSetting.miniProgramUrl" class="alipay_community">
          <div @mousemove="showAlipayApp = true" @mouseout="showAlipayApp = false">
            <img class="img" src="@/assets/images/QRcode.png" />
            <span class="txt">快捷报名</span>
          </div>
          <div v-show="showAlipayApp" class="B_QRcode">
            <a-qrcode class="QRcode" :value="aliPayQrcodeValue" :size="200" :bordered="false" errorLevel="H" :icon="AlipayQrIco" />
            <span class="B_txt">支付宝小程序快捷报名</span>
          </div>
        </div>
        <!-- 微信公众号二维码 -->
        <div v-if="webSystemSetting.weChatAppId" class="wx_community">
          <div @mousemove="showWeChatApp = true" @mouseout="showWeChatApp = false">
            <img class="img" src="@/assets/images/QRcode.png" />
            <span class="txt">扫码关注</span>
          </div>
          <div v-show="showWeChatApp" class="B_QRcode">
            <a-qrcode class="QRcode" :value="wxQrcodeValue" :size="200" :bordered="false" errorLevel="H" :icon="WechatQrIco" />
            <span class="B_txt">微信扫码关注公众号</span>
          </div>
        </div>
      </div>
    </a-layout-content>
    <Footer></Footer>
  </a-layout>
  <!-- appLoading -->
  <div v-if="appLoading" class="appLoading">
    <div class="appLoadingContent">
      <a-spin tip="加载中..." size="large" :spinning="appLoading"> </a-spin>
    </div>
  </div>

  <!-- 弹窗：修改初始密码 -->
  <LevelModal v-if="isOpendPasswordModal && !appLoading" title="账户安全提示！" text="用户您好，您已超1个月未更改登录密码，为了您的账户安全，请及时更改自己的登录密码。" class="LayerModal">
    <template #footer>
      <a-button class="btn" @click="closePasswordModal">暂不修改</a-button>
      <a-button class="btn" type="primary" @click="goToUpgradePassword()">立即修改</a-button>
    </template>
  </LevelModal>

  <!-- 弹窗：提升实名认证等级 -->
  <LevelModal v-if="isOpenAuthLevelModal && !appLoading" title="实名等级认证！" text="当前实名认证过低，请提升您的实名认证等级。" class="LayerModal">
    <template #footer>
      <a-button class="btn" @click="closeRealNameAuthLevelModal">暂不提升</a-button>
      <a-button class="btn" type="primary" @click="goToUpgradeRealNameAuthLevel()">立即提升</a-button>
    </template>
  </LevelModal>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, toRefs, createVNode } from "vue";
import { useRoute } from "vue-router";
import Navigation from "@/components/header/Navigation.vue";
import Footer from "@/components/footer/Footer.vue";
import { store } from "@/store";
import { clearStaleState, createOidcService, OpenIdConnectService } from "@/utils/oidcServer";
import type { NewUser } from "@/utils/oidcServer";
import { ExclamationCircleOutlined } from "@ant-design/icons-vue";
import { Modal } from "ant-design-vue";
import router from "@/router";
import login from "@/http/api/login";
import { session_storage, storage } from "@/utils/storage";
// import { BasicServiceClass } from "@/http/api/basicService";
import { MinioToolsClass } from "@/utils/minioToolsClass";
import { Loading } from "@/utils/loading";
import AlipayQrIco from "@/assets/images/alipay.png";
import WechatQrIco from "@/assets/images/wechat.png";
import LevelModal from "@/components/CustomModal/index.vue";

export default defineComponent({
  components: {
    Navigation,
    Footer,
    LevelModal,
  },
  setup() {
    const currentRoute = useRoute();
    onMounted(async () => {
      try {
        state.appLoading = true;
        state.appReady = false;
        event.watchStorageState();
        event.setSettings();
        event.getWeiXinQRcode();
        event.getAlipayQRcode();
        if (store.state.needInitOidc) {
          await event.initOidc();
        }
        state.appReady = true;
        state.appLoading = false;
      } catch (error) {
      } finally {
        state.appLoading = false;
        Loading.hide();
      }
    });
    const spinning = computed(() => store.state.loading);
    const current = ref<string[]>(["mail"]);
    const selectedKeys = computed(() => {
      return [useRoute().path];
    });
    const state = reactive({
      webSystemSetting: store.state.webSystemSetting,
      wxQrcodeValue: "",
      aliPayQrcodeValue: "",
      showWeChatApp: false,
      showAlipayApp: false,
      appReady: false,
      appLoading: false,
    });

    const isOpendPasswordModal = computed(() => store.state?.currentRegisterUser?.hintChangePassword && !store.state?.hiddenChangePassword); // 修改初始密码
    const isOpenAuthLevelModal = computed(() => store.state?.currentRegisterUser?.realNameAuthLevel < 4 && !store.state?.hiddenChangeAuthLevel); // 提升实名等级弹窗
    const topBackground = computed(() => MinioToolsClass.getMinioFileCompleteUrl(store.state.webSystemSetting.topBackground));
    const event = {
      async initOidc() {
        return new Promise((resolve, reject) => {
          // 创建OIDC服务
          const oidcService = createOidcService();
          oidcService
            .handleSigninSilent()
            .then(async (user) => {
              if (user) {
                await event.callLoginSucc(user);
                resolve(true);
              } else {
                sessionStorage.clear();
                throw new Error("OIDC未登录");
              }
            })
            .catch(async (error) => {
              try {
                console.warn("OIDC未登录");
                sessionStorage.clear();
                await this.callLoginFail();
              } catch (error) {
                reject(error);
              }
              resolve(false);
            });
        });
      },
      // 已登录回调
      async callLoginSucc(user: NewUser) {
        console.log("OIDC已登录");
      },
      // 未登录回调
      async callLoginFail() {
        OpenIdConnectService.setuser(undefined);
        await login.Login.GET.getTmpToken();
      },
      getWeiXinQRcode() {
        const APPID = state.webSystemSetting.weChatAppId;
        if (!APPID) return;
        const redirect_uri = encodeURIComponent(`${window.location.origin}/bindOpenId?token=${storage.get("storeIndex-token")}`);
        state.wxQrcodeValue = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${APPID}&redirect_uri=${redirect_uri}&response_type=code&scope=snsapi_base&state=1#wechat_redirect`;
        // console.log("微信公众号二维码", { wxQrcodeValue: state.wxQrcodeValue });
      },
      getAlipayQRcode() {
        const miniProgramUrl = state.webSystemSetting.miniProgramUrl || "";
        if (!miniProgramUrl) return;
        const tenantAccessId = state.webSystemSetting.id || "";
        state.aliPayQrcodeValue = miniProgramUrl + "/home?tenantAccessId=" + tenantAccessId;
        // console.log("支付宝小程序二维码", { aliPayQrcodeValue: state.aliPayQrcodeValue });
      },

      closePasswordModal() {
        store.commit("SET_DATA", { name: "hiddenChangePassword", data: true });
      },
      goToUpgradePassword() {
        const newHref = `${import.meta.env.VITE_OIDC_AUTHORITY}/modifyPassword?callback=${encodeURIComponent(window.location.origin)}`;
        window.location.href = newHref;
      },
      closeRealNameAuthLevelModal() {
        store.commit("SET_DATA", { name: "hiddenChangeAuthLevel", data: true });
      },
      goToUpgradeRealNameAuthLevel() {
        const newHref = `${import.meta.env.VITE_OIDC_AUTHORITY}/realNameAuthLevel?authLevel=4&callback=${encodeURIComponent(window.location.origin)}`;
        window.location.href = newHref;
      },
      // 监测缓存状态改变
      watchStorageState() {
        window.addEventListener("storage", function (e) {
          if (e.key == "storeIndex-userOperationID") {
            const userOperationID = storage.get("storeIndex-userOperationID");
            if (userOperationID && store.state.userOperationID && store.state.userOperationID != userOperationID && (currentRoute.name === "workFlowMain" || currentRoute.name === "positionlist")) {
              // console.log("检测到userOperationID发生变更", { old: store.state.userOperationID, new: userOperationID });
              Modal.destroyAll();
              Modal.info({
                title: "考试状态变更提醒",
                class: "changeExam",
                icon: createVNode(ExclamationCircleOutlined),
                centered: true,
                keyboard: false,
                maskClosable: false,
                content: createVNode("div", null, "检测到当前考试状态发生变更，请前往“我的报名”查看最新报考情况。"),
                okText: "查看我的报名",
                onOk() {
                  router.replace({ name: "exams" });
                },
              });
            }
          }
          // 登录用户改变
          if (e.key == "storeIndex-user") {
            const user = storage.get("storeIndex-user");
            if (user?.profile?.id && store.state.user?.profile?.id && store.state.user.profile.id != user.profile.id) {
              // console.log("登录账号发生变更", { old: store.state.user?.profile?.id, new: user.profile.id, store: store.state });
              Modal.destroyAll();
              Modal.info({
                title: "检测到登录账号发生变更",
                class: "changeLogin",
                icon: createVNode(ExclamationCircleOutlined),
                centered: true,
                keyboard: false,
                maskClosable: false,
                content: createVNode("div", null, "当前页面下有其他账号在浏览器或选项卡发生了登录操作，请前往首页重新进入。"),
                okText: "前往首页",
                // cancelText: "重新登录",
                onOk() {
                  session_storage.clear();
                  window.location.replace(router.resolve({ name: "currentExams" }).href);
                },
              });
            }
          }
          // 登录状态改变
          if (e.key == "storeIndex-isLogin") {
            const isLogin = storage.get("storeIndex-isLogin");
            if (isLogin === false && store.state.isLogin === true) {
              console.log("检测到用户退出了", isLogin);
              clearStaleState();
              window.location.replace(router.resolve({ name: "currentExams" }).href);
            }
          }
        });
      },
      // 获取系统设置和访问设置, 合并设置
      async setSettings() {
        event.setFavicon();
        event.setPath();
        event.setAccessCount();
      },
      // 根据配置显示网站title和favicon.ico
      setFavicon() {
        const { webSiteName, favicon } = store.state.webSystemSetting;
        const link = document.createElement("link");
        link.type = "image/x-icon";
        link.rel = "shortcut icon";
        link.href = MinioToolsClass.getMinioFileCompleteUrl(favicon);
        document.getElementsByTagName("head")[0].appendChild(link);
        document.title = webSiteName;
      },
      // 判断当前系统的访问地址是否正确（不包含测试环境192.168开头和localhost）
      setPath() {
        const { httpAddress } = store.state.webSystemSetting;
        if (window.location.origin != httpAddress && !window.location.origin.includes("192.168") && !window.location.origin.includes("localhost")) {
          router.replace({ path: "/404" });
        }
      },
      // 设置网站访问统计代码
      setAccessCount() {
        const { accessCount } = store.state.webSystemSetting;
        if (accessCount) {
          new Function(accessCount)();
        }
      },
    };

    return {
      current,
      spinning,
      selectedKeys,
      ...toRefs(state),
      ...event,
      AlipayQrIco,
      WechatQrIco,
      isOpendPasswordModal, // 修改初始密码
      isOpenAuthLevelModal, // 提升实名等级弹窗
      topBackground,
    };
  },
});
</script>

<style lang="less" scoped>
.LayerModal {
  .btn {
    min-width: 88px;
    margin: 0 10px;
  }
}
.appLoading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  .appLoadingContent {
    margin-top: -10vh;
  }
}
.appLayout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  .appHeader {
    height: auto;
    padding: 0;
    .wrap-topHead {
      width: 100%;
      min-width: 1100px;
      overflow: hidden;
      position: relative;
      background-color: #008de8;
      .topHeadImg {
        margin: 0 auto;
        position: relative;
        transform: translateX(-50%);
        left: 50%;
      }
    }
  }
  .wrap-navigation {
    position: sticky;
    top: 0;
    z-index: 10;
    left: 0;
  }
  .appContent {
    margin: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
    .mainLoading {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.75);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mainBody {
      width: 1100px;
      height: 100%;
      min-height: 100vh;
      margin: 0 auto;
      flex: 1;
      background: #fff;
      display: flex;
      flex-direction: column;
      position: relative;
    }
    .mainAffix {
      position: fixed;
      left: 50%;
      bottom: 100px;
      margin-left: 580px;
      z-index: 999;
      .wx_community {
        border: 1px solid rgba(0, 0, 0, 0.1);
        background: #fff;
        padding: 6px;
        position: relative;
        z-index: 2;
        cursor: default;
        margin-bottom: 10px;
        .txt {
          display: block;
          font-size: 12px;
          padding: 3px;
          width: 32px;
          color: #999;
          line-height: 1.2;
          text-align: center;
        }
        .B_QRcode {
          position: absolute;
          z-index: 3;
          right: 50px;
          bottom: 0;
          background: #fff;
          text-align: center;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
          .B_txt {
            font-size: 14px;
            padding: 0;
            display: block;
            padding: 0 15px 15px;
            text-align: center;
            color: #888;
            line-height: 1;
          }
        }
      }
      .alipay_community {
        border: 1px solid rgba(0, 0, 0, 0.1);
        background: #fff;
        padding: 6px;
        position: relative;
        z-index: 2;
        cursor: default;
        margin-bottom: 10px;
        .txt {
          display: block;
          font-size: 12px;
          padding: 3px;
          width: 32px;
          color: #999;
          line-height: 1.2;
          text-align: center;
        }
        .B_QRcode {
          position: absolute;
          z-index: 3;
          right: 50px;
          bottom: 0;
          background: #fff;
          text-align: center;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
          .B_txt {
            font-size: 14px;
            padding: 0;
            display: block;
            padding: 0 15px 15px;
            text-align: center;
            color: #888;
            line-height: 1;
          }
        }
      }
    }
  }
  .content::-webkit-scrollbar {
    width: 0 !important;
  }
  .sys-title {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #333;
  }
  .pr-108 {
    padding-right: 108px;
  }
}
:deep(.ant-layout-header) {
  background: #fff !important;
  padding: 0;
}
:deep(.ant-spin) {
  margin-top: 10vh;
}
.activeMenu {
  color: #1890ff;
}
</style>
